import { useState, useEffect, useContext } from "react"
import { MDBBtn, MDBBtnGroup, MDBCard, MDBCardBody, MDBCardImage, MDBCardText, MDBCardTitle, MDBCheckbox, MDBChip, MDBCol, MDBIcon, MDBInput, MDBInputGroup, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalFooter, MDBModalHeader, MDBModalTitle, MDBRadio, MDBRow, MDBSelect, MDBStepper, MDBStepperStep, MDBTypography, MDBValidationItem } from "mdb-react-ui-kit"
import { useTranslation } from "react-i18next"
import { generateClient } from 'aws-amplify/api';
import SelectTopics from "../../../../components-user/topics/SelectTopics";
import CountrySelect from "../../../../components/CountrySelect";
import { isoToGermanDate } from "../../../../utils/dateTools";


export default function PersonalInformation({
    user, event, params, setParams, useProfileAddressForBilling, setUseProfileAddressForBilling,
    billingAddress, setBillingAddress, personalAddress, setPersonalAddress, personalContact, setPersonalContact,
    customerType, setCustomerType, setCustomerTypeSetByUser, handleClickPrev, handleClickNext,
    validations, showValidations, setShowValidations, skipValidation, hideBadgePreview: hideBadgePreviewProp, hideSelectCustomerType }) {

    const hideBadgePreview = hideBadgePreviewProp || !event?.badgePreview?.fileName;
    const client = generateClient();
    const { t, i18n } = useTranslation()

    const [profiles, setProfiles] = useState([]);

    const updatePersonalAddress = (event) => {
        const path = event.target.attributes.datapath.value;
        const value = event.target.value;
        setPersonalAddress({ ...personalAddress, [path]: value });
    }

    const updateBillingAddress = (event) => {
        const path = event.target.attributes.datapath.value;
        const value = event.target.value;
        setBillingAddress({ ...billingAddress, [path]: value });
    }

    const updatePersonalContact = (event) => {
        const path = event.target.attributes.datapath.value;
        const value = event.target.value;
        setPersonalContact({ ...personalContact, [path]: value });
    }

    const scrollToFirstError = () => {
        setTimeout(() => {
            const firstInvalid = document.querySelector('.is-invalid');
            if (firstInvalid) {
                firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    };

    const handleClickButton = () => {
        if (skipValidation) {
            handleClickNext();
            return;
        }
        if (validations?.personalAddress?.length > 0) {
            setShowValidations(old => ({ ...old, personalAddress: true }));
        } else {
            setShowValidations(old => ({ ...old, personalAddress: false }));
        }

        if (validations?.personalContact?.length > 0) {
            setShowValidations(old => ({ ...old, personalContact: true }));
        } else {
            setShowValidations(old => ({ ...old, personalContact: false }));
        }

        if (!useProfileAddressForBilling && validations?.billingAddress?.length > 0) {
            setShowValidations(old => ({ ...old, billingAddress: true }));
        } else {
            setShowValidations(old => ({ ...old, billingAddress: false }));
        }

        if (validations?.personalAddress?.length === 0 && validations?.personalContact?.length === 0 &&
            (useProfileAddressForBilling || (!useProfileAddressForBilling && validations?.billingAddress?.length === 0))) {
            handleClickNext();
        } else {
            scrollToFirstError();
        }
    }

    return (

        <>
            {/* Address data from user profile */}
            <h4>{t("Your information")}</h4>
            <MDBRow>
                <MDBCol size="12" lg="8" className={hideBadgePreview ? "mx-auto" : ""}>
                    <MDBCard className="mt-2 mt-lg-4 col-lg-11 col-xl-10 mx-auto text-start shadow-sm">
                        <MDBCardBody className="p-2 p-sm-3 p-lg-4">
                            <h5>{t("Personal information")}</h5>
                            <MDBRow className="mb-3">
                                <MDBCol size="12">
                                    <MDBInput size="sm" required={customerType === "business" && useProfileAddressForBilling} className={showValidations?.personalAddress && validations?.personalAddress?.includes("company") ? "is-invalid" : ""} autoComplete="organization" name="organization" datapath="company" label={t("Company")} value={personalAddress.company || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                            </MDBRow>
                            <MDBRow className="my-3 gy-3">
                                <MDBCol size="12" md="6" xl="2">
                                    <MDBInput size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("title") ? "is-invalid" : ""} autoComplete="honorific-prefix" name="honorific-prefix" datapath="title" label={t("Title")} value={personalAddress?.title || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                                <MDBCol size="12" md="6" xl="5">
                                    <MDBInput required size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("firstName") ? "is-invalid" : ""} autoComplete="given-name" name="given-name" datapath="firstName" label={t("First Name")} value={personalAddress?.firstName || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                                <MDBCol size="12" md="6" xl="5">
                                    <MDBInput required size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("lastName") ? "is-invalid" : ""} autoComplete="family-name" name="family-name" datapath="lastName" label={t("Last name")} value={personalAddress?.lastName || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                            </MDBRow>
                            <MDBRow className="mb-3">
                                <MDBCol size="8">
                                    <MDBInput size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("street") ? "is-invalid" : ""} autoComplete="address-line1" name="address-line1" datapath="street" label={t("Street")} value={personalAddress?.street || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                                <MDBCol size="4">
                                    <MDBInput size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("streetNumber") ? "is-invalid" : ""} autoComplete="address-line2" name="address-line2" datapath="streetNumber" label={t("House Number")} value={personalAddress?.streetNumber || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                            </MDBRow>

                            <MDBRow className="mb-3">
                                <MDBCol size="4">
                                    <MDBInput size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("zip") ? "is-invalid" : ""} autoComplete="postal-code" name="postal-code" datapath="zip" label={t("Postal Code")} value={personalAddress?.zip || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                                <MDBCol size="8">
                                    <MDBInput size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("city") ? "is-invalid" : ""} autoComplete="address-level2" name="address-level2" datapath="city" label={t("City")} value={personalAddress?.city || ""} onChange={updatePersonalAddress} />
                                </MDBCol>
                            </MDBRow>
                            <MDBRow className="mb-3">
                                <MDBCol>

                                    <CountrySelect
                                        invalid={showValidations?.personalAddress && validations?.personalAddress?.includes("countryCode")}
                                        value={personalAddress?.countryCode || ""}
                                        size="sm" autoComplete="country" name="country" datapath="countryCode" onChange={updatePersonalAddress} />
                                </MDBCol>
                            </MDBRow>

                            {/* Start of Contact Data Section */}
                            <h5 className="mt-3">{t("Contact data")}</h5>
                            <MDBRow className="mb-3 gy-3">
                                <MDBCol size="12" md="6">
                                    <MDBInput
                                        className={showValidations?.personalContact && validations?.personalContact?.includes("phone") ? "is-invalid" : ""}
                                        autoComplete="tel"
                                        name="tel"
                                        datapath="phone"
                                        label={t("Phone")}
                                        value={personalContact?.phone || ""}
                                        onChange={updatePersonalContact}
                                    />
                                </MDBCol>
                                <MDBCol size="12" md="6">
                                    <MDBInput
                                        className={showValidations?.personalContact && validations?.personalContact?.includes("email") ? "is-invalid" : ""}
                                        autoComplete="email"
                                        name="email"
                                        datapath="email"
                                        type="email"
                                        label={t("Email")}
                                        value={personalContact?.email || ""}
                                        onChange={updatePersonalContact}
                                    />
                                </MDBCol>
                            </MDBRow>
                            {/* End of Contact Data Section */}

                            <h5 className="mt-3">{t("Billing information")}</h5>
                            <MDBCheckbox className="mb-2" label={t("Use the same address for billing")} checked={useProfileAddressForBilling} onChange={e => setUseProfileAddressForBilling(e.target.checked)} />
                            {!useProfileAddressForBilling &&
                                <div>
                                    <h6>{t("Billing Address")}</h6>
                                    <MDBRow className="mb-3">
                                        <MDBCol size="12">
                                            <MDBInput size="sm" required={customerType === "business"} className={showValidations?.billingAddress && validations?.billingAddress?.includes("company") ? "is-invalid" : ""} autoComplete="section-billing organization" name="billing-organization" datapath="company" label={t("Company")} value={billingAddress?.company || ""} onChange={updateBillingAddress} />
                                        </MDBCol>
                                    </MDBRow>
                                    <MDBRow className="mb-3 gy-3">
                                        <MDBCol size="12" md="6">
                                            <MDBInput size="sm" className={showValidations?.billingAddress && validations?.billingAddress?.includes("firstName") ? "is-invalid" : ""} autoComplete="section-billing given-name" name="billing-given-name" datapath="firstName" label={t("First Name")} value={billingAddress?.firstName || ""} onChange={updateBillingAddress} />
                                        </MDBCol>
                                        <MDBCol size="12" md="6">
                                            <MDBInput size="sm" className={showValidations?.billingAddress && validations?.billingAddress?.includes("lastName") ? "is-invalid" : ""} autoComplete="section-billing family-name" name="billing-family-name" datapath="lastName" label={t("Last name")} value={billingAddress?.lastName || ""} onChange={updateBillingAddress} />
                                        </MDBCol>
                                    </MDBRow>
                                    <MDBRow className="mb-3">
                                        <MDBCol size="8">
                                            <MDBInput size="sm" className={showValidations?.billingAddress && validations?.billingAddress?.includes("street") ? "is-invalid" : ""} autoComplete="section-billing address-line1" name="billing-address-line1" datapath="street" label={t("Street")} value={billingAddress?.street || ""} onChange={updateBillingAddress} />
                                        </MDBCol>
                                        <MDBCol size="4">
                                            <MDBInput size="sm" className={showValidations?.billingAddress && validations?.billingAddress?.includes("streetNumber") ? "is-invalid" : ""} autoComplete="off" name="billing-street-number" datapath="streetNumber" label={t("Number")} value={billingAddress?.streetNumber || ""} onChange={updateBillingAddress} />
                                        </MDBCol>
                                    </MDBRow>
                                    <MDBRow className="mb-3">
                                        <MDBCol size="4">
                                            <MDBInput size="sm" className={showValidations?.billingAddress && validations?.billingAddress?.includes("zip") ? "is-invalid" : ""} autoComplete="section-billing postal-code" name="billing-postal-code" datapath="zip" label={t("Zip")} value={billingAddress?.zip || ""} onChange={updateBillingAddress} />
                                        </MDBCol>
                                        <MDBCol size="8">
                                            <MDBInput size="sm" className={showValidations?.billingAddress && validations?.billingAddress?.includes("city") ? "is-invalid" : ""} autoComplete="section-billing address-level2" name="billing-address-level2" datapath="city" label={t("City")} value={billingAddress?.city || ""} onChange={updateBillingAddress} />
                                        </MDBCol>
                                    </MDBRow>
                                    <MDBRow className="mb-2">
                                        <MDBCol>
                                            <CountrySelect
                                                invalid={showValidations?.billingAddress && validations?.billingAddress?.includes("countryCode")}
                                                value={billingAddress?.countryCode || ""}
                                                size="sm" autoComplete="section-billing country" name="billing-country" datapath="countryCode" onChange={updateBillingAddress} />
                                        </MDBCol>
                                    </MDBRow>
                                </div>}
                            {!hideSelectCustomerType &&
                                <div className="mt-2">
                                    <span>{t("Invoice recipient")}: </span>
                                    <MDBRadio inline checked={customerType === "consumer"} id='radio-customerType-consumer' name='customerType' label={t("Private individual")} onChange={() => { setCustomerType("consumer"); setCustomerTypeSetByUser(true) }} />
                                    <MDBRadio inline checked={customerType === "business"} id='radio-customerType-business' name='customerType' label={t("Company")} onChange={() => { setCustomerType("business"); setCustomerTypeSetByUser(true) }} />
                                </div>
                            }

                            <h6 className="mt-2">{t("Optional")}:</h6>
                            <MDBRow className="mb-3">
                                <MDBCol>
                                    <MDBInput size="sm" className={showValidations?.personalAddress && validations?.personalAddress?.includes("billingEmail") ? "is-invalid" : ""} autoComplete="off" datapath="billingEmail" label={t("Billing email address")} value={params?.billingEmail || ""} onChange={(e) => setParams(old => ({ ...old, billingEmail: e.target.value }))} />
                                </MDBCol>
                            </MDBRow>

                        </MDBCardBody>
                    </MDBCard>


                </MDBCol>
                {!hideBadgePreview &&
                    <MDBCol size="12" lg="4">
                        {/* Preview */}
                        <MDBCard className="mt-2 mt-lg-4">
                            <MDBCardBody className="p-2 p-lg-4">
                                <h4>{t("Badge preview")}</h4>
                                <p>
                                    {t("The badge will be printed on the event and will be available for you at the entrance.")}
                                </p>
                                <div className="badge-preview-container position-relative text-center">
                                    <img
                                        src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/EventImages/" + event?.badgePreview?.fileName}
                                        alt="Badge preview"
                                        className="w-100"
                                    />
                                    <div className="position-absolute d-flex flex-column align-items-center justify-content-center" style={{ top: "30%", bottom: "12%", left: "5%", right: "5%" }}>
                                        <p className="mb-0" style={{ fontSize: "1.15em" }}><strong>{user.title && user.title} {user.firstName} {user.middleName} {user.lastName}</strong></p>
                                        <p className="mb-0">{user.contact?.address?.company}</p>
                                        <img src="https://iec-cp-public.s3.eu-central-1.amazonaws.com/dummy_qr_code.png" alt="QR-Code" style={{ width: "35%", maxWidth: "130px", marginTop: "2%" }} />
                                        <div className="d-flex align-items-center justify-content-center gap-1 mt-1">
                                            {[...(params.selectedDays || [])].sort().map((day) => (
                                                <span key={day} style={{ lineHeight: "1" }}>{isoToGermanDate(day).substring(0, 6)}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </MDBCardBody>
                        </MDBCard>
                    </MDBCol>
                }
            </MDBRow>


            {((showValidations?.personalAddress && validations?.personalAddress?.length > 0) ||
                (showValidations?.personalContact && validations?.personalContact?.length > 0) ||
                (showValidations?.billingAddress && validations?.billingAddress?.length > 0)) && (() => {
                    const fieldLabels = {
                        firstName: t("First Name"),
                        lastName: t("Last name"),
                        street: t("Street"),
                        streetNumber: t("House Number"),
                        zip: t("Postal Code"),
                        city: t("City"),
                        countryCode: t("Country"),
                        company: t("Company"),
                        title: t("Title"),
                        phone: t("Phone"),
                        email: t("Email"),
                    };
                    const getLabel = (field) => fieldLabels[field] || field;
                    return (
                        <div className="alert alert-danger mt-3 text-center">
                            <MDBIcon fas icon="exclamation-triangle" className="me-2" />
                            {t("Please fill out all required fields.")}
                            <div className="mt-2 small text-start">
                                {showValidations?.personalAddress && validations?.personalAddress?.length > 0 && (
                                    <div><strong>{t("Personal information")}:</strong> {validations.personalAddress.map(getLabel).join(", ")}</div>
                                )}
                                {showValidations?.personalContact && validations?.personalContact?.length > 0 && (
                                    <div><strong>{t("Contact data")}:</strong> {validations.personalContact.map(getLabel).join(", ")}</div>
                                )}
                                {showValidations?.billingAddress && validations?.billingAddress?.length > 0 && (
                                    <div><strong>{t("Billing Address")}:</strong> {validations.billingAddress.map(getLabel).join(", ")}</div>
                                )}
                            </div>
                        </div>
                    );
                })()}

            <div className="text-center mt-4">
                <MDBBtn color="primary" outline className="me-3" onClick={handleClickPrev}>{t("Back")}</MDBBtn>
                <MDBBtn color="primary" onClick={handleClickButton}>{t("Next")}</MDBBtn>
            </div>
        </>
    )
}